import { applySnapshot, types } from "mobx-state-tree"
import { flow } from "modules/common/models/flow"
import { Notificator } from "modules/common/models/notificator"
import { Queryable } from "modules/common/models/queryable"
import { Transport } from "modules/common/models/transport"
import { apiUrls } from "modules/common/services/communication/urls"
import { nameof } from "modules/common/services/typescript"


export const SignatureDto = types.model({
    rowVersion: types.number,
    signature: types.string
}).named("SignatureDto")

export type SignatureDtoType = typeof SignatureDto.Type

// Модель неподписанного документа
export const PendingSignatureItem = types
    .model({
        signatureId: types.string,
        orderId: types.string,
        orderNumber: types.string,
        orderName: types.string,
        contentGuid: types.string,
        contentName: types.string,
        version: types.number,
        signerType: types.enumeration("SignerType", ["worked", "checked", "control", "gip", "legal", "leader", ""]),
        signerTypeDisplay: types.string,
        fileName: types.string,
        fileId: types.string,
        createdAt: types.string,
        date: types.string,
        signatureSource: types.string,
        existingSignature: types.maybeNull(types.string)
    })
    .named("PendingSignatureItem")

export type PendingSignatureItemType = typeof PendingSignatureItem.Type

// Store для неподписанных документов
export const UserPendingSignaturesStore = types
    .compose(
        Transport,
        Notificator,
        Queryable,
        types.model({
            pendingSignatures: types.array(PendingSignatureItem),
            loading: types.boolean,
            totalCount: types.optional(types.number, 0),
            signingInProgress: types.optional(types.boolean, false),
        })
    )
    .views((self) => ({
        get filteredSignatures() {
            const predicate = (item: PendingSignatureItemType) => {
                let result = true
                if (self.pureQuery) {
                    const searchStr = `${item.orderNumber} ${item.orderName} ${item.contentName} ${item.fileName} ${item.signerTypeDisplay}`
                        .toLowerCase()
                    result = searchStr.indexOf(self.pureQuery.toLowerCase()) >= 0
                }
                return result
            }

            return self.pendingSignatures.filter(predicate)
        },

        get signaturesByType() {
            return {
                worked: self.pendingSignatures.filter((s) => s.signerType === "worked"),
                checked: self.pendingSignatures.filter((s) => s.signerType === "checked"),
                control: self.pendingSignatures.filter((s) => s.signerType === "control"),
                legal: self.pendingSignatures.filter((s) => s.signerType === "legal"),
            }
        },

        get hasPendingSignatures() {
            return self.pendingSignatures.length > 0
        },

        getSignaturesForOrder(orderId: string) {
            return self.pendingSignatures.filter((s) => s.orderId === orderId)
        },

        getSignaturesForContent(contentGuid: string) {
            return self.pendingSignatures.filter((s) => s.contentGuid === contentGuid)
        },
    }))
    .actions((self) => ({
        // Загрузка списка неподписанных документов
        loadPendingSignatures: flow(function* (params?: { orderId?: string; contentGuid?: string }) {
            try {
                self.loading = true

                const url = apiUrls.orders.contentTasks.userPendingSignatures

                const newData = yield self.transport.get<PendingSignatureItemType[]>(url)

                applySnapshot(self.pendingSignatures, newData)
                self.totalCount = newData.length

                return true
            } catch (er) {
                self.notify.error(er)
                return false
            } finally {
                self.loading = false
            }
        }),
        getSignature: flow(function* (signatureId: string, signatureSource?: string) {
            try {
                let result
                if (signatureSource === 'employee') {
                    result = yield self.transport.get(apiUrls.employee.getSignature(signatureId))
                } else {
                    result = yield self.transport.get(apiUrls.orders.contentTasks.getSignature(signatureId))
                }
                return ({
                    rowVersion: result.rowVersion,
                    signature: result.signature
                })
            } catch (error) {
                self.notify.error(`${(error as any).message}`)
                return null
            }
        }),
        // Отправка подписи на сервер
        submitSignature: flow(function* (
            signature: PendingSignatureItemType,
            // certificate: Certificate,
            signatureValue: string,
            rowVersion: number
        ) {
            try {
                self.signingInProgress = true
                // Определяем тип подписи по наличию orderId или signatureSource
                const isEmployeeSignature = !signature.orderId || signature.signatureSource === 'employee'

                const payload = {
                    signatureId: signature.signatureId,
                    signerType: signature.signerType,
                    fileId: signature.fileId,
                    signature: signatureValue,
                    // thumbprint: certificate.thumbprint,
                    signatureType: 'detached',
                    orderId: signature.orderId,
                    rowVersion: rowVersion,
                }

                let result
                if (isEmployeeSignature) {
                    result = yield self.transport.post(
                        apiUrls.employee.submitSignature,
                        payload
                    )
                } else {
                    result = yield self.transport.post(
                        apiUrls.orders.contentTasks.submitSignature,
                        payload
                    )
                }
                if (result) {
                    // Удаляем подпись из списка неподписанных
                    const index = self.pendingSignatures.findIndex(
                        (s) => s.signatureId === signature.signatureId && s.signerType === signature.signerType
                    )
                    if (index > -1) {
                        self.pendingSignatures.splice(index, 1)
                        self.totalCount = self.pendingSignatures.length
                    }

                    self.notify.success("Документ успешно подписан")
                }

                return result
            } catch (er) {
                self.notify.error(`Ошибка отправки подписи: ${er}`)
                throw er
            } finally {
                self.signingInProgress = false
            }
        }),

        // Очистка списка
        clear: () => {
            applySnapshot(self.pendingSignatures, [])
            self.totalCount = 0
        },

        // Обновление статуса после подписания (упрощенная версия без вызова API)
        markAsSigned: (signatureId: string, signerType: string) => {
            const index = self.pendingSignatures.findIndex(
                (s) => s.signatureId === signatureId && s.signerType === signerType
            )
            if (index > -1) {
                self.pendingSignatures.splice(index, 1)
                self.totalCount = self.pendingSignatures.length
            }
            self.notify.success("Документ успешно подписан")
        },
    }))
    .named("UserPendingSignaturesStore")

export type UserPendingSignaturesStoreType = typeof UserPendingSignaturesStore.Type
export type UserPendingSignaturesStoreSnapshotType = typeof UserPendingSignaturesStore.SnapshotType

// Начальное состояние
export const initialState = (): UserPendingSignaturesStoreSnapshotType => ({
    pendingSignatures: [],
    loading: false,
    totalCount: 0,
    pureQuery: "",
    query: "",
    signingInProgress: false,
})

// Поля для сортировки и фильтрации
export const fields = {
    orderNumber: nameof((a: PendingSignatureItemType) => a.orderNumber) as string,
    orderName: nameof((a: PendingSignatureItemType) => a.orderName) as string,
    contentName: nameof((a: PendingSignatureItemType) => a.contentName) as string,
    fileName: nameof((a: PendingSignatureItemType) => a.fileName) as string,
    signerTypeDisplay: nameof((a: PendingSignatureItemType) => a.signerTypeDisplay) as string,
    version: nameof((a: PendingSignatureItemType) => a.version) as string,
    createdAt: nameof((a: PendingSignatureItemType) => a.createdAt) as string,
}